import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'allAgentsButton'
	| 'seeAllAgentsButton'
	| 'searchPlaceholder'
	| 'searchTemplatesPlaceholder'
	| 'favouritedAgentLabel'
	| 'agentDeletedSuccessFlagTitle'
	| 'agentDeletedSuccessFlagDescription'
	| 'agentDeletedErrorFlagTitle'
	| 'agentDeletedErrorFlagDescription'
	| 'ownerChangedSuccessFlagTitle'
	| 'ownerChangedSuccessFlagDescription'
	| 'developmentSectionTitle'
	| 'templatesTitle'
	| 'templatesDescription'
	| 'templatesDescriptionRefresh'
	| 'betaLozengeTitle'
	| 'loadingAgents'
	| 'searchingAgents'
	| 'noResults'
	| 'loadMoreButtonReady'
	| 'loadMoreButtonLoading';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		allAgentsButton: {
			id: 'rovo-chat.browse-agents.all-agents-button',
			defaultMessage: 'All',
			description: 'Button label to show all agents in the browse-agents panel',
		},
		seeAllAgentsButton: {
			id: 'rovo-chat.browse-agents.see-all-agents-button',
			defaultMessage: 'See all agents',
			description: 'Button to see all agents, shown on the empty view when no agents are starred',
		},
		searchPlaceholder: {
			id: 'rovo-chat.browse-agents.search-input.placeholder',
			defaultMessage: 'Search agents',
			description: 'Placeholder for the chat view search input',
		},
		searchTemplatesPlaceholder: {
			id: 'rovo-chat.browse-agents.search-templates-input.placeholder',
			defaultMessage: 'Search templates',
			description: 'Placeholder for the chat view search templates input',
		},
		favouritedAgentLabel: {
			id: 'rovo-chat.browse-agents.favourited-agent-label',
			defaultMessage: 'Favourited agent',
			description: 'Accessible label indicating the agent has been favourited by the user',
		},
		agentDeletedSuccessFlagTitle: {
			id: 'ai-mate.agent-list.delete-agent-success-flag-title',
			defaultMessage: 'Agent deleted',
			description: 'Flag message title displayed when an agent is deleted',
		},
		agentDeletedSuccessFlagDescription: {
			id: 'ai-mate.agent-list.delete-agent-success-flag-description',
			defaultMessage: 'You deleted {agentName}',
			description: 'Flag message description displayed when an agent is deleted',
		},
		agentDeletedErrorFlagTitle: {
			id: 'ai-mate.agent-list.delete-agent-error-flag-title',
			defaultMessage: 'Failed to delete agent',
			description: 'Flag message title displayed when there is an error deleting agent',
		},
		agentDeletedErrorFlagDescription: {
			id: 'ai-mate.agent-list.delete-agent-error-flag-description',
			defaultMessage: 'Something went wrong while deleting the agent. Try again in a few moments.',
			description: 'Flag message description displayed when there is an error deleting agent',
		},
		ownerChangedSuccessFlagTitle: {
			id: 'ai-mate.agent-list.change-owner-success-flag-title',
			defaultMessage: 'Owner changed successfully',
			description: 'Flag message title displayed when an agent owner is changed',
		},
		ownerChangedSuccessFlagDescription: {
			id: 'ai-mate.agent-list.change-owner-success-flag-description',
			defaultMessage: 'You changed the owner of {agentName} to {newOwnerName}',
			description: 'Flag message description displayed when an agent owner is changed',
		},
		developmentSectionTitle: {
			id: 'ai-mate.agent-list.development-section-title',
			defaultMessage: 'Development agents',
			description: 'Title of the section with all agents',
		},
		templatesTitle: {
			id: 'ai-mate.agent-list.templates-title',
			defaultMessage: 'Coding templates',
			description: 'Title of the section with templates',
		},
		templatesDescription: {
			id: 'ai-mate.agent-list.templates-description',
			defaultMessage:
				'Use these templates to create your own developer agent. Pick the right agent template for your work, select Use template, then fill in the template prompt with specific details like repository, folder, language, and library. Create tailored development agents for coding different tasks, projects, or repositories.',
			description: 'Description of the templates section',
		},
		templatesDescriptionRefresh: {
			id: 'ai-mate.agent-list.templates-description-refresh',
			defaultMessage:
				'Use these templates to create your own developer agent. Pick the right agent template for your work, select Use template, then fill in the template prompt with specific details like repository, folder, language, and library. Create tailored development agents for coding different tasks, spaces, or repositories.',
			description: 'Description of the templates section',
		},
		betaLozengeTitle: {
			id: 'ai-mate.agent-list.beta-lozenge-title',
			defaultMessage: 'Beta',
			description: 'Title of the lonzenge that indicates a beta feature',
		},
		loadingAgents: {
			id: 'ai-mate.agent-list.loading-agents',
			defaultMessage: 'Loading agents...',
			description: 'Message to display when agents are loading',
		},
		searchingAgents: {
			id: 'ai-mate.agent-list.searching-agents',
			defaultMessage: 'Searching for agents...',
			description: 'Message to display when agents are searching',
		},
		noResults: {
			id: 'ai-mate.agent-list.no-results',
			defaultMessage: 'No results found for "{searchValue}"',
			description: 'Message to display when no agents are found',
		},
		loadMoreButtonReady: {
			id: 'ai-mate.agent-list.load-more-button-ready',
			defaultMessage: 'Load more',
			description: 'Message to display when the load more button is ready',
		},
		loadMoreButtonLoading: {
			id: 'ai-mate.agent-list.load-more-button-loading',
			defaultMessage: 'Loading more agents...',
			description: 'Message to display when the load more button is loading',
		},
	});

export default message;
